

import { useMemo } from 'react';
import './animations.css';
import { Globe, ToggleLeft, ToggleRight } from 'lucide-react';
import { type TabItem } from '@/src/components/ui/spotlight/types/tab.types';

interface CampaignRenderProps {
    searchQuery: string;
    campaigns: any[];
    campaignFilter: 'active' | 'archived' | 'all';
    handleCampaignFilterClick: () => void;
    isFetchingCampaigns: boolean;
    tabItems: TabItem[];
    onClose: () => void;
}

export default function CampaignRender({
    searchQuery,
    campaigns,
    campaignFilter,
    handleCampaignFilterClick,
    isFetchingCampaigns,
    tabItems,
    onClose
}: CampaignRenderProps) {
    if (!searchQuery) return null;

    // Split search query into terms and filter out empty strings
    const searchTerms = useMemo(() => searchQuery.toLowerCase().split(/\s+/).filter(term => term.length > 0), [searchQuery]);

    // Filter campaigns based on search terms and filter state
    const filteredCampaigns = useMemo(() => campaigns
        .filter(campaign => {
            const campaignName = campaign.name.toLowerCase();
            // Check if all search terms are found in the campaign name
            return searchTerms.every(term => campaignName.includes(term)) &&
                (campaignFilter === 'all' || campaign.status === campaignFilter);
        })
        .sort((a, b) => {
            // When showing all campaigns, sort active ones first
            if (campaignFilter === 'all') {
                if (a.status === 'active' && b.status !== 'active') return -1;
                if (a.status !== 'active' && b.status === 'active') return 1;
            }
            // Default sort by name
            return a.name.localeCompare(b.name);
        }), [campaigns, searchTerms, campaignFilter]);

    return (
        <div>
            <div className="flex items-center gap-3">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Campaigns
                </h3>
                <button
                    onClick={handleCampaignFilterClick}
                    className={`px-2 py-0.5 text-xs font-medium rounded-full transition-colors cursor-pointer inline-flex items-center gap-1.5
          ${campaignFilter === 'active'
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/50'
                            : campaignFilter === 'archived'
                                ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 hover:bg-orange-200 dark:hover:bg-orange-900/50'
                                : 'bg-gray-100 text-gray-700 dark:bg-gray-700/30 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700/50'
                        }`}
                >
                    {campaignFilter === 'active'
                        ? 'Displaying active campaigns'
                        : campaignFilter === 'archived'
                            ? 'Displaying archived campaigns'
                            : 'Displaying all campaigns'}
                    {campaignFilter === 'active' ? (
                        <ToggleRight className="w-3 h-3" />
                    ) : campaignFilter === 'archived' ? (
                        <ToggleLeft className="w-3 h-3" />
                    ) : (
                        <div className="flex items-center gap-0.5">
                            <ToggleRight className="w-3 h-3 text-green-600 dark:text-green-400" />
                            <ToggleLeft className="w-3 h-3 text-orange-600 dark:text-orange-400" />
                        </div>
                    )}
                </button>
                <div className="h-[1px] flex-1 bg-gradient-to-r from-gray-200 to-transparent dark:from-gray-700" />
            </div>
            {isFetchingCampaigns && (
                <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400" data-testid="spotlight-campaigns-searching">
                    Searching campaigns…
                </div>
            )}
            {filteredCampaigns.length > 0 && !isFetchingCampaigns && (
                <div className="mt-3 overflow-y-auto max-h-[450px] rounded-lg border border-gray-200 dark:border-gray-700 [scrollbar-width:thin]" style={{
                    scrollbarWidth: 'thin',
                }}>
                    <div className="divide-y divide-gray-200 dark:divide-gray-700">
                        {filteredCampaigns.map((campaign) => (
                            <div
                                key={campaign._id}
                                className="p-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors campaign-item campaign-item-visible"
                                data-testid={`spotlight-campaign-item-${campaign._id}`}
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-3">
                                        <div className="flex-shrink-0">
                                            {campaign.trafficSource?.logo ? (
                                                <img
                                                    src={campaign.trafficSource.logo}
                                                    alt={campaign.trafficSource.name}
                                                    className="h-6 w-6 rounded"
                                                />
                                            ) : (
                                                <Globe className="h-6 w-6 text-gray-400" />
                                            )}
                                        </div>
                                        <div>
                                            <a
                                                href={`/campaigns/edit/${campaign.trafficSource?.api?.name?.toLowerCase()}/${campaign._id}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="hover:text-blue-600 dark:hover:text-blue-400"
                                            >
                                                <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                                    {campaign.name}
                                                </h4>
                                            </a>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                                {campaign.trafficSource?.name || 'No traffic source'}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex flex-col items-end gap-2">
                                        <div className="flex items-center space-x-2">
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium
                        ${campaign.status === 'active' ?
                                                    'bg-green-100 text-green-800 dark:bg-green-800/20 dark:text-green-400' :
                                                    campaign.status === 'archived' ?
                                                        'bg-red-100 text-red-800 dark:bg-red-800/20 dark:text-red-400' :
                                                        'bg-gray-100 text-gray-800 dark:bg-gray-800/20 dark:text-gray-400'}`}>
                                                {campaign.status}
                                            </span>
                                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium
                        ${campaign.remoteCampaign?.status === 'active' ?
                                                    'bg-green-100 text-green-800 dark:bg-green-800/20 dark:text-green-400' :
                                                    'bg-gray-100 text-gray-800 dark:bg-gray-800/20 dark:text-gray-400'}`}>
                                                <span className={`w-2 h-2 rounded-full ${campaign.remoteCampaign?.status === 'active' ?
                                                    'bg-green-500 dark:bg-green-400' :
                                                    'bg-gray-400 dark:bg-gray-500'
                                                    }`} />
                                                Remote
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            {tabItems.map((tab) => {
                                                const Icon = tab.icon;
                                                const baseUrl = `/campaigns/edit/${campaign.trafficSource?.api?.name?.toLowerCase()}/${campaign._id}`;
                                                return (
                                                    <a
                                                        key={tab.value}
                                                        href={`${baseUrl}?target=${tab.value}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                                                        title={tab.label}
                                                    >
                                                        <Icon className="w-4 h-4 text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400" />
                                                    </a>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            {!filteredCampaigns.length && !isFetchingCampaigns && (
                <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
                    No campaigns found
                </div>
            )}
        </div>
    );
} 
